// Vendor portal API client. A SEPARATE axios instance with its own Bearer token
// store, so the vendor session never mixes with the operator session. The
// vendor token is persisted (sessionStorage) so a reload keeps the vendor in.

import axios, { type AxiosInstance } from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '';
const TOKEN_KEY = 'iosentra_vendor_token';

let vendorToken: string | null = sessionStorage.getItem(TOKEN_KEY);

export function setVendorToken(token: string | null): void {
  vendorToken = token;
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}
export function getVendorToken(): string | null {
  return vendorToken;
}

const vapi: AxiosInstance = axios.create({ baseURL: API_BASE, withCredentials: true });
vapi.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  if (vendorToken) config.headers.Authorization = `Bearer ${vendorToken}`;
  config.headers['X-Requested-With'] = 'iosentra-vendor';
  return config;
});

export interface VendorSession { id: string; name: string; email: string; allowedTenantIds: string[]; role?: string }
/** Compact site location for Navigate + context (from the vendor projection). */
export interface SiteLite { id: string; name: string; address: string | null; lat: number | null; lng: number | null }
export interface VendorTicket {
  id: string; tenantId: string; tenantName: string; ticketNumber: string; title: string;
  description: string | null; status: string; priority: string; severity: string | null;
  slaDeadline: string | null; slaResolutionDeadline?: string | null; slaBreached: boolean; source: string | null;
  firstRespondedAt?: string | null; assignmentStatus?: string | null; leadTechnicianId?: string | null;
  escalationLevel?: number; siteId?: string | null; site?: SiteLite | null; allowedTransitions?: string[];
  assignedTo: string | null; assignedTechnicianId: string | null; createdAt: string; resolvedAt: string | null;
  comments?: unknown; activityLog?: unknown; attachments?: unknown;
}
export interface VendorSummary {
  orgs: number; open: number; inProgress: number; breaching: number; resolved7d: number;
  byOrg: Array<{ tenantId: string; tenantName: string; open: number; breaching: number }>;
}
export interface VendorTechnician {
  id: string; tenantId: string; name: string; email: string | null; phone: string | null; skills: string[]; status: string;
  mobilePhone?: string | null; operationalStatus?: string; employmentStatus?: string | null;
  canReceiveAssignments?: boolean; isEmergencyTechnician?: boolean; maximumConcurrentTickets?: number | null;
  serviceRegionIds?: string[]; supportedCustomerIds?: string[]; workload?: number;
}
export interface VendorShift {
  id: string; tenantId: string; technicianId: string; kind: string; shiftType?: string; recurrenceRule?: string | null;
  startsAt: string; endsAt: string; siteId: string | null; notes: string | null;
}
export interface OnShiftTech { technicianId: string; name: string; kind: string }
export interface VendorTeam {
  id: string; tenantId: string; name: string; description: string | null; isActive: boolean;
  teamLeaderTechnicianId?: string | null; supportedCategoryIds?: string[]; supportedRegionIds?: string[];
  supportedSiteIds?: string[]; maximumConcurrentTickets?: number | null;
}
export interface VendorTeamMember { id: string; teamId: string; technicianId: string; membershipType: string; isActive: boolean }
export interface CalendarShift { id: string; technicianId: string; kind: string; shiftType: string; startsAt: string; endsAt: string; source: string; siteId: string | null }
export interface ShiftConflict { aId?: string; bId?: string; technicianId: string; overlapStart: string; overlapEnd: string }
export interface ShiftCalendar { shifts: CalendarShift[]; conflicts: ShiftConflict[] }
export interface Rotation { id: string; tenantId: string; name: string; rotationType: string; startAt: string; isActive: boolean }
export interface OnCallNow { rotationId: string; at: string; technicianId: string | null; name: string | null }
export interface VendorDashboard {
  orgs: number;
  queue: {
    awaitingAcceptance: number; unassigned: number; manualRequired: number; atRisk: number; breached: number; activeAssignments: number;
    oldestAwaitingMs?: number | null; nextBreachMs?: number | null; oldestBreachMs?: number | null;
  };
  offers: { pending: number; oldestPendingMs?: number | null };
  techStatus: Record<string, number>;
  byCustomer: Array<{ tenantId: string; tenantName: string; open: number; awaiting: number; breaching: number; technicians: number }>;
}
export interface Offer {
  id: string; ticketId: string; offerVersion: number; offeredAt: string; expiresAt: string;
  expiresInMs?: number; ticketNumber?: string | null; title?: string | null; priority?: string | null;
  slaDeadline?: string | null; site?: SiteLite | null;
}
export interface AssignedJob {
  id: string; ticketNumber?: string; title: string; status: string; priority: string;
  slaDeadline: string | null; assignmentStatus?: string | null; siteId: string | null;
  site?: SiteLite | null; allowedTransitions?: string[];
}
export interface MyWork {
  technician: { id: string; name: string; operationalStatus: string };
  onShift: boolean;
  offers: Offer[];
  assigned: AssignedJob[];
}
// ── Parity types (mirror apps/mobile/src/lib/vendor-api.ts) ──────────────────
export interface DispatchQueueItem {
  offerId: string; ticketId: string; tenantId: string; tenantName: string;
  ticketNumber: string; title: string; priority: string; status: string; assignmentStatus: string | null;
  slaDeadline: string | null; slaBreached: boolean;
  technicianId: string | null; technicianName: string | null;
  offerStatus: string; attemptNumber: number; offerVersion: number;
  offeredAt: string; expiresAt: string | null; ageMs: number; expiresInMs: number | null;
  rejectionReasonCode: string | null; rejectionReasonText: string | null;
}
export interface EligibleTechnician {
  technicianId: string; name: string; skills: string[]; onShift: boolean; activeTickets: number;
  operationalStatus: string; eligible: boolean; rejectionReasons: string[];
  totalScore: number | null; recommended: boolean;
}
export interface ManagerNotification {
  id: string; kind: string; title: string; tenantId: string; tenantName: string;
  ticketId?: string; ticketNumber?: string; severity: 'info' | 'warning' | 'critical'; at: string;
}
export interface VendorAnalytics {
  days: number; orgs: number;
  sla: { resolved: number; inSla: number; compliancePct: number | null };
  offers: { total: number; accepted: number; rejected: number; expired: number; acceptanceRatePct: number | null; avgTimeToAcceptMs: number | null };
  byCustomer: Array<{ tenantId: string; tenantName: string; resolved: number; slaCompliance: number | null; acceptanceRate: number | null }>;
}
export interface TechnicianLocation {
  technicianId: string; tenantId: string; name: string; operationalStatus: string;
  lat: number; lng: number; at: string;
}
export interface OfferRejectReason { code: string; label: string }
export interface ActivityType { id: string; name: string; code: string; sortOrder: number }

const data = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export const vendorApi = {
  async login(email: string, password: string): Promise<{ accessToken: string; vendor: { id: string; name: string; email: string; orgCount: number } }> {
    const res = await vapi.post('/api/v1/vendor/login', { email, password });
    setVendorToken(res.data.accessToken);
    return res.data;
  },
  me: () => data<VendorSession>(vapi.get('/api/v1/vendor/me').then((r) => ({ data: { data: r.data } }))),
  logout: () => setVendorToken(null),

  tickets: (params?: {
    status?: string; priority?: string; tenantId?: string;
    search?: string; assignedTechnicianId?: string; assignmentStatus?: string;
    slaRisk?: boolean; slaBreached?: boolean;
  }) =>
    data<VendorTicket[]>(vapi.get('/api/v1/vendor/tickets', {
      params: {
        ...(params ?? {}),
        ...(params?.slaRisk ? { slaRisk: 'true' } : {}),
        ...(params?.slaBreached ? { slaBreached: 'true' } : {}),
      },
    })),
  ticket: (tenantId: string, id: string) =>
    data<VendorTicket>(vapi.get(`/api/v1/vendor/tickets/${tenantId}/${id}`)),
  patchTicket: (tenantId: string, id: string, body: { status?: string; assignedTechnicianId?: string | null; comment?: string; resolution?: string }) =>
    data<VendorTicket>(vapi.patch(`/api/v1/vendor/tickets/${tenantId}/${id}`, body)),
  summary: () => data<VendorSummary>(vapi.get('/api/v1/vendor/summary')),

  technicians: (tenantId?: string, opts?: { withWorkload?: boolean }) =>
    data<VendorTechnician[]>(vapi.get('/api/v1/vendor/technicians', {
      params: { ...(tenantId ? { tenantId } : {}), ...(opts?.withWorkload ? { withWorkload: 'true' } : {}) },
    })),
  createTechnician: (body: { tenantId: string; name: string; email?: string; phone?: string; skills?: string[] }) =>
    data<{ id: string }>(vapi.post('/api/v1/vendor/technicians', body)),
  shifts: (tenantId?: string) =>
    data<VendorShift[]>(vapi.get('/api/v1/vendor/shifts', { params: tenantId ? { tenantId } : {} })),
  createShift: (body: { tenantId: string; technicianId: string; kind?: string; startsAt: string; endsAt: string; notes?: string; shiftType?: string; recurrenceRule?: string; timezone?: string }) =>
    data<{ id: string }>(vapi.post('/api/v1/vendor/shifts', body)),
  onShift: (tenantId: string, at?: string) =>
    data<OnShiftTech[]>(vapi.get('/api/v1/vendor/on-shift', { params: { tenantId, ...(at ? { at } : {}) } })),

  // ── Technician teams (Phase 1) ────────────────────────────────────────────
  teams: (tenantId?: string) =>
    data<VendorTeam[]>(vapi.get('/api/v1/vendor/teams', { params: tenantId ? { tenantId } : {} })),
  createTeam: (body: { tenantId: string; name: string; description?: string }) =>
    data<{ id: string }>(vapi.post('/api/v1/vendor/teams', body)),
  teamMembers: (teamId: string, tenantId: string) =>
    data<VendorTeamMember[]>(vapi.get(`/api/v1/vendor/teams/${teamId}/members`, { params: { tenantId } })),
  addTeamMember: (teamId: string, body: { tenantId: string; technicianId: string; membershipType?: string }) =>
    data<{ id: string }>(vapi.post(`/api/v1/vendor/teams/${teamId}/members`, body)),

  // ── Scheduling (Phase 2) ──────────────────────────────────────────────────
  shiftCalendar: (tenantId: string, from: string, to: string) =>
    data<ShiftCalendar>(vapi.get('/api/v1/vendor/shift-calendar', { params: { tenantId, from, to } })),
  rotations: (tenantId?: string) =>
    data<Rotation[]>(vapi.get('/api/v1/vendor/rotations', { params: tenantId ? { tenantId } : {} })),
  createRotation: (body: { tenantId: string; name: string; startAt: string; rotationType?: string; rotationRule?: string }) =>
    data<{ id: string }>(vapi.post('/api/v1/vendor/rotations', body)),
  rotationMembers: (rotationId: string, tenantId: string) =>
    data<Array<{ id: string; technicianId: string; sequence: number }>>(vapi.get(`/api/v1/vendor/rotations/${rotationId}/members`, { params: { tenantId } })),
  addRotationMember: (rotationId: string, body: { tenantId: string; technicianId: string; sequence: number }) =>
    data<{ id: string }>(vapi.post(`/api/v1/vendor/rotations/${rotationId}/members`, body)),
  onCallNow: (rotationId: string, tenantId: string, at?: string) =>
    data<OnCallNow>(vapi.get(`/api/v1/vendor/rotations/${rotationId}/on-call`, { params: { tenantId, ...(at ? { at } : {}) } })),

  // ── Dashboard + technician workspace (Phase 5) ────────────────────────────
  dashboard: () => data<VendorDashboard>(vapi.get('/api/v1/vendor/dashboard')),
  myWork: (tenantId: string, technicianId: string) =>
    data<MyWork>(vapi.get('/api/v1/vendor/my-work', { params: { tenantId, technicianId } })),
  setMyStatus: (body: { tenantId: string; technicianId: string; operationalStatus: string }) =>
    data<{ operationalStatus: string }>(vapi.patch('/api/v1/vendor/my-status', body)),
  acceptOffer: (offerId: string, body: { tenantId: string; technicianId: string; offerVersion: number }) =>
    data<{ accepted: boolean; runId?: string }>(vapi.post(`/api/v1/vendor/offers/${offerId}/accept`, body)),
  rejectOffer: (offerId: string, body: { tenantId: string; technicianId: string; reasonCode?: string; reasonText?: string }) =>
    data<{ rejected: boolean }>(vapi.post(`/api/v1/vendor/offers/${offerId}/reject`, body)),
  offerRejectReasons: () => data<OfferRejectReason[]>(vapi.get('/api/v1/vendor/offer-reject-reasons')),
  activityTypes: (tenantId: string) => data<ActivityType[]>(vapi.get('/api/v1/vendor/activity-types', { params: { tenantId } })),
  logActivity: (tenantId: string, id: string, body: { activityTypeCode?: string; note?: string; durationMinutes?: number }) =>
    data<{ logged: boolean }>(vapi.post(`/api/v1/vendor/tickets/${tenantId}/${id}/activity`, body)),
  requestAssistance: (tenantId: string, id: string, body: { technicianId: string; kind: string; note?: string }) =>
    data<{ requested: boolean; kind: string }>(vapi.post(`/api/v1/vendor/tickets/${tenantId}/${id}/request-assistance`, body)),
  attachmentUrl: (tenantId: string, id: string, key: string) =>
    data<{ url: string }>(vapi.get(`/api/v1/vendor/tickets/${tenantId}/${id}/attachments/url`, { params: { key } })).then((d) => d.url),

  // ── Manager mode (parity with mobile) ─────────────────────────────────────
  dispatchQueue: (status: 'awaiting' | 'rejected' | 'expired' | 'cancelled') =>
    data<DispatchQueueItem[]>(vapi.get('/api/v1/vendor/dispatch-queue', { params: { status } })),
  eligibleTechnicians: (tenantId: string, id: string) =>
    data<{ recommendedTechnicianId: string | null; technicians: EligibleTechnician[] }>(
      vapi.get(`/api/v1/vendor/tickets/${tenantId}/${id}/eligible-technicians`)),
  assignTechnician: (tenantId: string, id: string, technicianId: string) =>
    data<{ assigned: boolean; reassigned: boolean }>(
      vapi.post(`/api/v1/vendor/tickets/${tenantId}/${id}/assign`, { technicianId })),
  cancelOffer: (tenantId: string, id: string) =>
    data<{ cancelled: number }>(vapi.post(`/api/v1/vendor/tickets/${tenantId}/${id}/cancel-offer`, {})),
  managerNotifications: () => data<ManagerNotification[]>(vapi.get('/api/v1/vendor/manager-notifications')),
  analytics: (days?: number) => data<VendorAnalytics>(vapi.get('/api/v1/vendor/analytics', { params: days ? { days } : {} })),
  technicianLocations: (tenantId?: string) =>
    data<TechnicianLocation[]>(vapi.get('/api/v1/vendor/technician-locations', { params: tenantId ? { tenantId } : {} })),
};
